import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import appConstants from '../../../../../constants';
import { KEYS } from '../../../../../l18n/constants';
import computeClassName from '../../../../../utils/computeClassName';
import handleEventClick from '../../../../../utils/handleEventClick';
import navigateToURL from '../../../../../utils/navigateToURL';
import { useL18nContext } from '../../../ContextProvider/L18n';
import { useUserContext } from '../../../ContextProvider/User';
import { usePartnerSearchContext } from '../../../ContextProvider/Partner/Search';
import { useNotificationsContext } from "../../../ContextProvider/Notifications";
import CCIconChevronRight16x16 from '../../../../libs/CiscoCore/Icon/Chevron/Right/16x16';
import IconHeartSolid from '../../../Icon/Heart/Solid';
import IconHeart from '../../../Icon/Heart';
import PartnerSavedStatusIcon from '../../../Partner/Saved/StatusIcon';
import {useNavigationBetweenPartnerFeaturesContext} from "../../../ContextProvider/Navigation/BetweenPartnerFeatures";


const constants = Object.freeze({
  CLASSNAME_BASE: '',
  COMPONENT_IS_SAVED: {
    true: IconHeartSolid,
    false: IconHeart
  }
});

export default function PartnerProfileNavigationAndToolbar(
  {
    partnerDetails,
    className = ''
  }
) {
  const computedClassName = computeClassName(className, constants.CLASSNAME_BASE);

  const history = useHistory();
  function navigateBack() { history.push('/'); }

  const { getTranslationFor } = useL18nContext();
  const translatedLabelSave = getTranslationFor(KEYS.L18N_KEY_LABEL_SAVE);
  const translatedLabelRemove = getTranslationFor(KEYS.L18N_KEY_LABEL_REMOVE);
  const translatedLabelBack = getTranslationFor(KEYS.L18N_KEY_LABEL_BACK);
  const translatedTextProcessingInProgress = getTranslationFor(KEYS.L18N_KEY_LABEL_PROCESSING_IN_PROGRESS);

  const {
    user,
    userSavedPartnerSiteIds,
    userSavedPartners,
    isUserPartnerOperationInProgress,
    saveUserPartner,
    removeUserPartner,
    isUserSavedPartnersFetchingInProgress,
    setRefetchUserSavedPartners
  } = useUserContext();

  const {
    updatePartnerSavedCount,
    comparedItemList,
    removeItemFromComparator,
    persistAppState
  } = usePartnerSearchContext();

  const {
    notifyMustLogInFn,
    notifySuccessfulUserPartnerSavingFn,
    notifyFailedUserPartnerSavingFn,
    notifySuccessfulUserPartnerDeletionFn,
    notifyFailedUserPartnerDeletionFn,
  } = useNotificationsContext();

  const {selectPartnerSearchFeature} = useNavigationBetweenPartnerFeaturesContext();

  const partnerBeGeoId = partnerDetails?.be_geo_id||0;
  const partnerSiteId = partnerDetails?.site_id||0;
  const isPartnerSaved = user.isLoggedIn() ? userSavedPartnerSiteIds.indexOf(`${partnerSiteId}`) !== -1 : partnerDetails?.isSaved||false;
  const partnerFavTrackId = userSavedPartners.filter(userSavedPartner => userSavedPartner[appConstants.KEY_SITE_ID_LOWERCASE] === partnerSiteId)[0]?.favTrackId||-1;

  const computedBackClickFn = handleEventClick(navigateBack);

  const [isLocalSavingOperationInProgress, setIsLocalSavingOperationInProgress] = useState(false);

  function beforeLogin() {
    persistAppState(partnerDetails?.site_id||null);
    navigateToURL(appConstants.SSO_LOGIN_URL);
  }

  const computedSavePartnerButtonClickFn =
    user.isLoggedIn() ?
      (event) => {
        event.preventDefault();
        if (!isLocalSavingOperationInProgress) {
          setIsLocalSavingOperationInProgress(true);
          saveUserPartner(
            user.getId(),
            partnerBeGeoId,
            partnerSiteId,
            () => {
              updatePartnerSavedCount(partnerDetails, 1);
              notifySuccessfulUserPartnerSavingFn();
              setRefetchUserSavedPartners(true);
              setIsLocalSavingOperationInProgress(false);
            },
            notifyFailedUserPartnerSavingFn
          );
        }
      } :
      handleEventClick(notifyMustLogInFn, beforeLogin);

  const computedRemovePartnerButtonClickFn =
    user.isLoggedIn() ?
      (event) => {
        event.preventDefault();
        if (!isLocalSavingOperationInProgress) {
          setIsLocalSavingOperationInProgress(true);
          removeUserPartner(
            user.getId(),
            partnerFavTrackId,
            () => {
              updatePartnerSavedCount(partnerDetails, -1);
              notifySuccessfulUserPartnerDeletionFn();
              setRefetchUserSavedPartners(true);
              setIsLocalSavingOperationInProgress(true);
            },
            notifyFailedUserPartnerDeletionFn
          );
        }

        // Remove partner from comparator in case it was being compared
        // (removal function uses a filter so no need to search for existence prior to removal).
        removeItemFromComparator(partnerDetails, appConstants.KEY_SITE_ID_LOWERCASE);
        // Make sure to navigate user back to search if # of compared items is less than the acceptable minimum.
        if(comparedItemList.length - 1 < appConstants.COMPARATOR_MIN_ITEM_COUNT_LIMIT) {
          selectPartnerSearchFeature();
        }
      } :
      handleEventClick(notifyMustLogInFn, beforeLogin);

  return (
    <section className={computedClassName}>
      <div className="px-5 xs:px-0 xs:mx-auto xs:w-8/12 flex flex-row items-center justify-between text-cc-body-medium sm:text-cc-body-large">
        <nav>
          <ul className="flex flex-row items-center space-x-2">
            <li className="flex flex-row items-center">
              <a href="/"
                 className="transition-colors duration-200 ease-linear group inline-flex items-center text-cc-accent font-semibold hover:text-cc-accent-60"
                 onClick={computedBackClickFn}>
                <CCIconChevronRight16x16 aria-hidden="true" className="w-3 h-3 md:w-4 md:h-4 transform-gpu -rotate-180"/>
                <span className="ml-cc-0.5char transition-colors duration-200 border-b border-transparent group-hover:border-cc-accent">{translatedLabelBack}</span>
              </a>
            </li>
          </ul>
        </nav>
        <a href="/"
           onClick={isPartnerSaved ? computedRemovePartnerButtonClickFn : computedSavePartnerButtonClickFn}
           className="transition-colors duration-200 ease-linear group inline-flex items-center text-cc-accent font-semibold hover:text-cc-accent-60">
          <span className="transition-colors duration-200 border-b border-transparent group-hover:border-cc-accent">{ isPartnerSaved ? translatedLabelRemove : translatedLabelSave }</span>
          <PartnerSavedStatusIcon className="ml-cc-1char transform-gpu -translate-y-px"
                                  isSaved={isPartnerSaved}
                                  isSavingOrRemovingInProgress={isUserPartnerOperationInProgress||isUserSavedPartnersFetchingInProgress}
                                  savingOrRemovingInProgressText={translatedTextProcessingInProgress}/>
        </a>
      </div>
    </section>

  );

}
